import { ArrowUpRightIcon } from 'lucide-react'

import { Banner, TextButton } from 'slab'
import {
  type MemberOnboardingMemberPlanChangesProps,
  type MemberOnboardingRestrictions,
} from 'utils/web_api.schemas'

import {
  isCurrentDentalPlanOffThatch,
  isMaxCoverageOverThreeYears,
} from '../../utils'
import { PartialCoverageWarningBanner } from '../PartialCoverageWarningBanner/PartialCoverageWarningBanner'
import RenewalsPlanComparisonHeader from '../RenewalsPlanComparisonHeader/RenewalsPlanComparisonHeader'

export interface QLEDentalPlanCardProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  hasFamily: boolean;
  restrictions: MemberOnboardingRestrictions;
}

export const QLEDentalPlanCard = ({
  planChangeSummary,
  hasFamily,
  restrictions,
}: QLEDentalPlanCardProps) => {
  const currentYear = planChangeSummary.current_plan_year
  const nextYear = planChangeSummary.next_plan_year
  const currentDentalPlan = currentYear.dental_plan
  const nextYearDentalPlan = nextYear.dental_plan

  const offThatchDental = isCurrentDentalPlanOffThatch(planChangeSummary)

  // Use current year plan if dental plan is off-thatch, otherwise use next year plan
  const planToUse = offThatchDental ? currentDentalPlan : nextYearDentalPlan

  if (!planToUse) {
    return null
  }

  let annualMaxCoverageLabel = 'Annual max coverage'
  if (isMaxCoverageOverThreeYears(planToUse.annual_max_coverage)) {
    annualMaxCoverageLabel = 'Max coverage over 3 years'
  }

  // Get the values to display
  const monthlyPremium = planToUse.premium_amount.formatted_amount
  const deductible = planToUse.deductible
  const annualMaxCoverage = planToUse.annual_max_coverage

  const planCannotBeChanged =
    restrictions?.dental_not_available === 'coverage_ends_mid_year'

  // Don't show "View full plan details" link for off-thatch plans
  const viewFullPlanDetailsUrl = !planCannotBeChanged && !offThatchDental && planToUse.sbc_url
    ? planToUse.sbc_url
    : undefined

  return (
    <div className='flex flex-col gap-6 border border-gray-200 rounded-2xl shadow-sm p-4 xs:p-6'>
      <RenewalsPlanComparisonHeader
        plan={planToUse}
        planType='dental'
      />
      {offThatchDental && (
        <Banner
          title='You purchased this plan outside of Thatch.'
          type='notice'
        />
      )}
      <PartialCoverageWarningBanner
        planChangeSummary={planChangeSummary}
        coverageType='dental'
      />
      <div className={`grid gap-4 ${offThatchDental ? 'grid-cols-1' : 'grid-cols-3'}`}>
        <div className='flex flex-col'>
          <div className='body-sm text-gray-500 mb-2'>Monthly premium</div>
          <div className='body text-gray-800'>{monthlyPremium}</div>
        </div>
        {!offThatchDental && (
          <>
            <div className='flex flex-col'>
              <div className='body-sm text-gray-500 mb-2'>
                {hasFamily && planToUse.category === 'bsca_dental'
                  ? 'Family deductible'
                  : 'Deductible'}
              </div>
              <div className='body text-gray-800'>{deductible}</div>
            </div>
            <div className='flex flex-col'>
              <div className='body-sm text-gray-500 mb-2'>
                {annualMaxCoverageLabel}
              </div>
              <div className='body text-gray-800'>{annualMaxCoverage}</div>
            </div>
          </>
        )}
      </div>
      {viewFullPlanDetailsUrl && (
        <TextButton
          onClick={() => {
            window.open(viewFullPlanDetailsUrl, '_blank')
          }}
          type='button'
          rightIcon={ArrowUpRightIcon}
        >
          View full plan details
        </TextButton>
      )}
    </div>
  )
}
